import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { usePageMeta } from '../seo';
import { track } from '../tracking/tracker';
import { useStorefront } from '../storefront/StorefrontContext';
import { BlockList } from '../storefront/Blocks';
import { FALLBACK_BLOCKS } from '../storefront/types';
import { LayerSlot } from '../layers/LayerSlot';

/**
 * Domovska stranka.
 *
 * Obsah sklada obsluha v administraci – tahle komponenta uz jen vykresli,
 * co prislo. Vyjimka je hledani: kdyz zakaznik neco hleda, ozdobne bloky
 * se schovaji a zustane mrizka s vysledky. Prochazet hero banner cestou
 * k vysledkum hledani je otrava.
 */
export function CatalogPage() {
  const [params] = useSearchParams();
  const search = params.get('hledat') ?? '';
  const category = params.get('kategorie') ?? '';
  const { blocks, loading } = useStorefront();

  /*
   * Nazev vybrane kategorie pro <title>. Driv se stahoval CELY seznam
   * kategorii a hledalo se v nem – pri 204 kategoriich ze stromu dodavatele
   * je to desitky kilobajtu kvuli jednomu retezci. Navic by to u kategorie,
   * ktera ma zbozi jen v podkategoriich, nazev vubec nenaslo:
   * `/api/shop/categories` vraci jen kategorie s PRIMO zarazenym zbozim.
   */
  const [categoryName, setCategoryName] = useState<string | null>(null);

  useEffect(() => {
    if (category === '') {
      setCategoryName(null);
      return;
    }
    let alive = true;
    void api
      .categoryNavigation(category)
      .then((result) => {
        if (alive) setCategoryName(result.category.name);
      })
      .catch(() => {
        if (alive) setCategoryName(null);
      });
    return () => {
      alive = false;
    };
  }, [category]);

  // CDP: prohlídka kategorie se váže na URL (?kategorie=), takže odkazy
  // i zpětné tlačítko počítají stejně jako přímá navigace.
  useEffect(() => {
    if (category !== '') track('category_viewed', { category_slug: category });
  }, [category]);

  /*
   * Vysledek hledani se NEINDEXUJE. Je jich nekonecne mnoho, obsah je
   * podmnozina katalogu a vyhledavac by si z nich udelal tisic stranek
   * s tymz zbozim.
   */
  usePageMeta({
    title:
      search !== ''
        ? `Hledání „${search}“ | Super Krmivo`
        : categoryName !== null
          ? `${categoryName} | Super Krmivo`
          : 'Super Krmivo – chovatelské potřeby',
    description:
      categoryName !== null
        ? `${categoryName} – krmivo a chovatelské potřeby skladem, doprava do druhého dne.`
        : 'Krmivo a chovatelské potřeby pro psy, kočky a další mazlíčky. Doprava do druhého dne.',
    index: search === '',
  });

  const searching = search !== '';
  const visible = searching ? blocks.filter((block) => block.kind === 'products') : blocks;

  // Kdyz obsluha smaze i mrizku zbozi, hledani by nemelo kam vypsat
  // vysledky. Zalozni blok je tu proto, aby vyhledavani fungovalo vzdycky.
  const rendered = searching && visible.length === 0 ? FALLBACK_BLOCKS : visible;

  if (loading) return <div className="page-loading" aria-hidden="true" />;

  // Pásy „Novinky" a „Nejprodávanější" jsou teď editovatelné bloky výlohy
  // (productRow se zdrojem newest/bestsellers), takže si je obsluha může
  // přesunout v builderu. Prázdný pás se nevykreslí.
  return (
    <>
      {category !== '' && <LayerSlot name="category_above_list" />}
      <BlockList blocks={rendered} />
      {!searching && category === '' && <LayerSlot name="home_below_carousel" />}
    </>
  );
}
